import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import DeleteIcon from '@material-ui/icons/Delete';
import DescriptionIcon from '@material-ui/icons/Description';
import NoteIcon from '@material-ui/icons/Note';
import moment from 'moment';
import { request } from 'utilities/graph';
import { updateElderServiceEvent, adminUpdateOrder } from 'graphql/mutations';
import { asyncListAll } from 'utilities/graph';
import { getOrdersByElderByDate } from './queries';
import { elderServiceEventOptions } from '@silvergatedelivery/constants';
import cache from 'utilities/cache';
import OrderCard from './OrderCard';
import { elderServiceEventConfig } from './helpers';
import { sortBy } from 'utilities/sorting';
import Alert from '@material-ui/lab/Alert';
import elderDeactivationReason from '@silvergatedelivery/constants/src/elderDeactivationReason';

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 700,
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  eventHeader: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    textAlign: 'center',
  },
  eventTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  eventSubtitle: {
    opacity: 0.9,
    fontSize: '1rem',
  },
  eventDetails: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    'fontSize': '1.1rem',
    'fontWeight': 600,
    'marginBottom': theme.spacing(2),
    'color': theme.palette.text.primary,
    'display': 'flex',
    'alignItems': 'center',
    '& svg': {
      marginRight: theme.spacing(1),
      fontSize: '1.2rem',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
    flexDirection: 'column',
  },
  loadingText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  description: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info[50],
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.info[200]}`,
  },
  note: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning[50],
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.warning[200]}`,
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  summary: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info[50],
    borderRadius: theme.spacing(1),
  },
}));

export default function DeleteElderServiceEventDialog({
  open,
  onClose,
  event,
  onSuccess,
}) {
  const classes = useStyles();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!open || !event) return;

    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        let dateCondition;
        if (event.endedAt) {
          const start = moment(event.startedAt).format('YYYY-MM-DD');
          const end = moment(event.endedAt).subtract(1, 'day').format('YYYY-MM-DD');
          dateCondition = {
            between: [start, end],
          };
        } else {
          dateCondition = {
            gt: moment(event.startedAt).subtract(1, 'day').format('YYYY-MM-DD'),
          };
        }

        const allOrders = (await asyncListAll(getOrdersByElderByDate, {
          elderId: event.elderId,
          date: dateCondition,
          filter: {
            status: {
              eq: 'cancelled',
            },
          },
        })).filter((order) => {
          return order.status === 'cancelled' && order.cancellationReason === elderServiceEventConfig[event.type].cancellationReason;
        });

        setOrders(allOrders.sort(sortBy('date')));
      } catch (error) {
        console.error('查詢訂單失敗:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!elderServiceEventConfig[event.type]) {
      setIsLoading(false);
      setOrders([]);
      return;
    }

    fetchOrders();
  }, [open, event]);

  const getEventOption = (type) => {
    return elderServiceEventOptions.find((option) => option.id === type) || {
      label: '未知類型',
      color: '#999',
      icon: null,
    };
  };

  const handleConfirmDelete = async () => {
    if (!event) return;

    setIsDeleting(true);
    try {
      const now = moment().toISOString();
      const userName = cache.get('app:userName');

      if (orders.length > 0) {
        const orderOperationsData = orders.map((order) => ({
          id: order.id,
          restaurantId: order.restaurantId,
          deliveryBy: order.deliveryBy,
          status: order.deliveryStaffId ? 'ready' : 'waitingForDeliveryStaff',
          tier: order.tier,
          cancellationReason: undefined,
          cancellationNote: undefined,
        }));

        await request(adminUpdateOrder, { input: { orders: orderOperationsData } });
      }

      await request(updateElderServiceEvent, {
        input: {
          id: event.id,
          status: '已刪除',
          updatedAt: now,
          updatedBy: userName,
        },
      });

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('刪除失敗:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!open || !event) return null;

  const dateString = event.endedAt ? (
    `${moment(event.startedAt).format('YYYY年MM月DD日')} - ${moment(event.endedAt).subtract(1, 'day').format('YYYY年MM月DD日')}`
  ) : (
    moment(event.startedAt).format('YYYY年MM月DD日')
  );

  let reason = event.description;
  if (event.type === 'serviceStop') {
    const deactivationReason = elderDeactivationReason.find((reason) => reason.value === event.description);
    reason = deactivationReason ? deactivationReason.label : event.description;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <DialogContent className={classes.dialogContent}>
        <div
          className={classes.eventHeader}
          style={{
            background: `linear-gradient(135deg, ${getEventOption(event.type).color}20 0%, ${getEventOption(event.type).color}40 100%)`,
            color: getEventOption(event.type).color,
            border: `2px solid ${getEventOption(event.type).color}30`,
          }}
        >
          <Typography className={classes.eventTitle}>
            {getEventOption(event.type).label}
          </Typography>
          <Typography className={classes.eventSubtitle}>
            {event.endedAt ? (<>
              {dateString}
              <span style={{ marginLeft: 8, fontWeight: 600 }}>
                ({moment(event.endedAt).diff(moment(event.startedAt), 'days')}天)
              </span></>) : (
              <>{dateString}</>
            )}
          </Typography>
        </div>

        <Divider className={classes.divider} />

        {reason && (
          <div className={classes.description}>
            <Typography className={classes.sectionTitle}>
              <DescriptionIcon />
              原因
            </Typography>
            <Typography variant="body1">
              {reason}
            </Typography>
          </div>
        )}

        {event.note && (
          <div className={classes.note}>
            <Typography className={classes.sectionTitle}>
              <NoteIcon />
              備註
            </Typography>
            <Typography variant="body1">
              {event.note}
            </Typography>
          </div>
        )}

        {orders.length > 0 && (
          <>
            <Divider className={classes.divider} />

            <div className={classes.eventDetails}>
              <Typography className={classes.sectionTitle}>
                相關訂單 ({orders.length})
              </Typography>
              {isLoading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress size={40} />
                  <Typography className={classes.loadingText}>
                    載入訂單中...
                  </Typography>
                </div>
              ) : (
                <>
                  <Grid container spacing={2}>
                    {orders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        isSelected={false}
                        onToggleSelection={() => {}}
                        cardSelectable={false}
                      />
                    ))}
                  </Grid>
                  <div className={classes.summary}>
                    <Alert severity="info">
                      將重置 {orders.length} 張已取消的訂單
                    </Alert>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button onClick={onClose} disabled={isDeleting}>
          取消
        </Button>
        <Button
          onClick={handleConfirmDelete}
          variant="contained"
          color="secondary"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          {isDeleting ? '處理中...' : '確認刪除'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DeleteElderServiceEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.object,
  onSuccess: PropTypes.func,
};
